import { useState, useEffect, useMemo, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  Chip,
  Button,
  TextField,
  Alert,
  InputAdornment,
  Skeleton,
  Paper,
  Divider,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  alpha,
} from "@mui/material";
import SendToMobileRoundedIcon from "@mui/icons-material/SendToMobileRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import CommuteRoundedIcon from "@mui/icons-material/CommuteRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import {
  getFinkedaGateways,
  finkedaQuote,
  finkedaInitiate,
} from "../api";

/* ---------------------------------- helpers ---------------------------------- */

const formatINR = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? "—"
    : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const NETWORKS = ["VISA", "MASTERCARD", "RUPAY"];
const NETWORK_LABELS = {
  VISA: "Visa",
  MASTERCARD: "Mastercard",
  RUPAY: "RuPay",
};
const CARD_TYPES = ["CREDITCARD", "CORPORATE"];
const CARD_TYPE_LABELS = {
  CREDITCARD: "Credit Card",
  CORPORATE: "Corporate Card",
};
const ACCT_TYPES = ["SAVINGS", "CURRENT"];

/* Friendly, de-branded metadata per payment purpose */
const PURPOSE_META = {
  education: { Icon: SchoolRoundedIcon, desc: "Tuition & education fees" },
  transport: { Icon: CommuteRoundedIcon, desc: "Travel & transport" },
  wallet: { Icon: AccountBalanceWalletRoundedIcon, desc: "Wallet load" },
  utility: { Icon: BoltRoundedIcon, desc: "Electricity & utility bills" },
  payall: { Icon: PaymentsRoundedIcon, desc: "General payments" },
};

const purposeMeta = (purpose) => {
  const key = String(purpose || "").toLowerCase().replace(/[^a-z]/g, "");
  return (
    PURPOSE_META[key] || { Icon: PaymentsRoundedIcon, desc: "General payments" }
  );
};

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/* ------------------------------ debounce hook ------------------------------ */

function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* A single label / value line inside the breakdown card */
function BreakdownRow({ label, value, strong, muted }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
      <Typography
        variant="body2"
        color={muted ? "text.secondary" : "text.primary"}
        sx={{ fontWeight: strong ? 700 : 400 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: strong ? 800 : 600, whiteSpace: "nowrap" }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/* ================================== page ================================== */

export default function FinkedaPay() {
  /* gateways */
  const [gateways, setGateways] = useState([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(true);
  const [gatewaysError, setGatewaysError] = useState(null);
  const [gatewayKey, setGatewayKey] = useState("");

  /* selections */
  const [network, setNetwork] = useState("");
  const [cardType, setCardType] = useState("");

  /* form */
  const [amount, setAmount] = useState("");
  const [selfPct, setSelfPct] = useState("");
  const [binValue, setBinValue] = useState("");
  const [sender, setSender] = useState({
    senderName: "",
    senderAccount: "",
    senderIfsc: "",
    senderBank: "",
    senderMobile: "",
    senderPanNumber: "",
    senderAccType: "SAVINGS",
  });

  /* quote */
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  /* submit */
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState(null);

  /* ------------------------------ data loading ------------------------------ */

  useEffect(() => {
    getFinkedaGateways()
      .then(({ data }) => setGateways(data || []))
      .catch((e) =>
        setGatewaysError(
          e.response?.data?.error || "Failed to load payment options",
        ),
      )
      .finally(() => setGatewaysLoading(false));
  }, []);

  /* Hide test / inactive gateways from the picker */
  const visibleGateways = useMemo(
    () =>
      gateways.filter((g) => {
        if (g.gatewayKey === "finkeda_silver") return false;
        const p = String(g.purpose || "").toLowerCase();
        if (p.includes("uat") || p.includes("test")) return false;
        return true;
      }),
    [gateways],
  );

  const selectedGateway = useMemo(
    () => gateways.find((g) => g.gatewayKey === gatewayKey) || null,
    [gateways, gatewayKey],
  );

  /* Networks / card types available for the selected gateway's charges */
  const availableNetworks = useMemo(() => {
    if (!selectedGateway) return [];
    const set = new Set(
      (selectedGateway.charges || []).map((c) => String(c.network).toUpperCase()),
    );
    return NETWORKS.filter((n) => set.has(n));
  }, [selectedGateway]);

  const availableCardTypes = useMemo(() => {
    if (!selectedGateway || !network) return [];
    const set = new Set(
      (selectedGateway.charges || [])
        .filter((c) => String(c.network).toUpperCase() === network)
        .map((c) => String(c.cardType).toUpperCase()),
    );
    return CARD_TYPES.filter((t) => set.has(t));
  }, [selectedGateway, network]);

  /* Per-transaction limit for the chosen network (max across its card types) */
  const networkLimit = useMemo(() => {
    if (!selectedGateway || !network) return null;
    const limits = (selectedGateway.charges || [])
      .filter((c) => String(c.network).toUpperCase() === network)
      .map((c) => Number(c.txnLimit))
      .filter((n) => n > 0);
    return limits.length ? Math.max(...limits) : null;
  }, [selectedGateway, network]);

  /* The charge row matching the current network + card type */
  const selectedCharge = useMemo(() => {
    if (!selectedGateway || !network || !cardType) return null;
    return (
      (selectedGateway.charges || []).find(
        (c) =>
          String(c.network).toUpperCase() === network &&
          String(c.cardType).toUpperCase() === cardType,
      ) || null
    );
  }, [selectedGateway, network, cardType]);

  /* Reset downstream selections when a gateway changes */
  const handleSelectGateway = (key) => {
    setGatewayKey(key);
    setNetwork("");
    setCardType("");
    setQuote(null);
    setQuoteError(null);
  };

  /* When network changes, clear card type if it's no longer valid */
  useEffect(() => {
    if (cardType && !availableCardTypes.includes(cardType)) {
      setCardType("");
    }
  }, [availableCardTypes, cardType]);

  /* --------------------------------- quote ---------------------------------- */

  const amountNum = Number(amount);
  const amountValid = amount !== "" && amountNum > 0;

  const debAmount = useDebounced(amount, 400);
  const debSelfPct = useDebounced(selfPct, 400);

  const runQuote = useCallback(() => {
    if (!gatewayKey || !network || !cardType || !(Number(debAmount) > 0)) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    setQuoteError(null);
    const body = {
      gatewayKey,
      network,
      cardType,
      amount: Number(debAmount),
    };
    if (debSelfPct !== "" && !isNaN(Number(debSelfPct))) {
      body.selfPct = Number(debSelfPct);
    }
    finkedaQuote(body)
      .then(({ data }) => {
        if (cancelled) return;
        setQuote(data || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setQuote(null);
        setQuoteError(e.response?.data?.error || "Unable to fetch a quote");
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gatewayKey, network, cardType, debAmount, debSelfPct]);

  useEffect(() => {
    const cleanup = runQuote();
    return cleanup;
  }, [runQuote]);

  const breakdown = quote?.breakdown || null;

  /* ------------------------------- validation ------------------------------- */

  const senderMobileOk = /^[6-9]\d{9}$/.test(sender.senderMobile.trim());
  const senderIfscOk = IFSC_REGEX.test(sender.senderIfsc.trim().toUpperCase());
  const senderPanOk = PAN_REGEX.test(sender.senderPanNumber.trim().toUpperCase());
  const binOk = binValue === "" || /^\d{6}$/.test(binValue);

  const senderComplete =
    sender.senderName.trim().length >= 3 &&
    sender.senderAccount.trim().length >= 6 &&
    senderIfscOk &&
    sender.senderBank.trim().length >= 2 &&
    senderMobileOk &&
    senderPanOk &&
    !!sender.senderAccType;

  const canProceed =
    amountValid &&
    !!gatewayKey &&
    !!network &&
    !!cardType &&
    senderComplete &&
    binOk &&
    !quoteError;

  /* --------------------------------- submit --------------------------------- */

  const handleProceed = async () => {
    if (!canProceed) return;
    setSubmitting(true);
    const body = {
      gatewayKey,
      network,
      cardType,
      amount: amountNum,
      sender: {
        senderName: sender.senderName.trim(),
        senderAccount: sender.senderAccount.trim(),
        senderIfsc: sender.senderIfsc.trim().toUpperCase(),
        senderBank: sender.senderBank.trim(),
        senderMobile: sender.senderMobile.trim(),
        senderPanNumber: sender.senderPanNumber.trim().toUpperCase(),
        senderAccType: sender.senderAccType,
      },
    };
    if (selfPct !== "" && !isNaN(Number(selfPct))) body.selfPct = Number(selfPct);
    if (binValue !== "") body.binValue = binValue;
    try {
      const { data } = await finkedaInitiate(body);
      if (data?.redirectionUrl) {
        setSnack({
          severity: "success",
          msg: "Redirecting to the secure payment page…",
        });
        window.location.href = data.redirectionUrl;
      } else {
        setSnack({
          severity: "warning",
          msg: "Payment initiated but no redirect URL was returned.",
        });
        setSubmitting(false);
      }
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to initiate the payment",
      });
      setSubmitting(false);
    }
  };

  const updateSender = (field) => (e) =>
    setSender((s) => ({ ...s, [field]: e.target.value }));

  /* -------------------------------- renderers -------------------------------- */

  const renderGateways = () => {
    if (gatewaysLoading) {
      return (
        <Grid container spacing={2}>
          {[...Array(3)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={104} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      );
    }
    if (visibleGateways.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <PaymentsRoundedIcon
            sx={{ fontSize: 46, color: "text.disabled", mb: 1 }}
          />
          <Typography color="text.secondary">
            No payment options are available right now
          </Typography>
        </Box>
      );
    }
    return (
      <Grid container spacing={2}>
        {visibleGateways.map((g) => {
          const selected = g.gatewayKey === gatewayKey;
          const color = "#1c6b8c";
          const { Icon, desc } = purposeMeta(g.purpose);
          return (
            <Grid item xs={12} sm={6} md={4} key={g.gatewayKey}>
              <Paper
                variant="outlined"
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => handleSelectGateway(g.gatewayKey)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectGateway(g.gatewayKey);
                  }
                }}
                sx={{
                  p: 2,
                  height: "100%",
                  cursor: "pointer",
                  borderRadius: 3,
                  borderWidth: 2,
                  transition: "all .15s ease",
                  borderColor: selected ? color : "divider",
                  bgcolor: selected ? alpha(color, 0.06) : "background.paper",
                  outline: "none",
                  "&:hover": { borderColor: color },
                  "&:focus-visible": {
                    borderColor: color,
                    boxShadow: `0 0 0 3px ${alpha(color, 0.2)}`,
                  },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color,
                      bgcolor: alpha(color, 0.12),
                      flexShrink: 0,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={700} fontSize={15} noWrap>
                      {g.purpose || "Payment"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {desc}
                    </Typography>
                  </Box>
                  {selected && (
                    <CheckCircleRoundedIcon sx={{ color, fontSize: 22, flexShrink: 0 }} />
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderBreakdown = () => {
    if (!gatewayKey || !network || !cardType) {
      return (
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 3, textAlign: "center", height: "100%" }}
        >
          <ReceiptLongRoundedIcon
            sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            Choose a gateway, network and card type, then enter an amount to see
            the live breakdown.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 3, height: "100%" }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <ReceiptLongRoundedIcon sx={{ fontSize: 20, color: "primary.main" }} />
          <Typography fontWeight={700} fontSize={14}>
            Quote breakdown
          </Typography>
          {quoteLoading && <CircularProgress size={15} sx={{ ml: "auto" }} />}
        </Stack>

        {selectedCharge?.txnLimit != null && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Per-transaction limit for {NETWORK_LABELS[network] || network}{" "}
            {CARD_TYPE_LABELS[cardType] || cardType}:{" "}
            <b>{formatINR(selectedCharge.txnLimit)}</b>
          </Typography>
        )}

        {quoteError && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 1.5 }}>
            {quoteError}
          </Alert>
        )}

        {!amountValid && !quoteError && (
          <Typography variant="body2" color="text.secondary">
            Enter an amount to see the charges and the amount paid to the bank
            account.
          </Typography>
        )}

        {breakdown && amountValid && (() => {
          const entries = breakdown.commissionEntries || [];
          const self = Number(
            entries.find((c) => c.kind === "self")?.amount || 0,
          );
          /* Charges = the gateway's own charge + every commission in the chain
             except the payer's own (self). The admin's override is a BUNDLED
             rate that already includes the gateway charge, so gateway charge +
             admin margin + any network commissions are presented as one figure. */
          const charges =
            Number(breakdown.finkedaChargeAmt || 0) +
            entries
              .filter((c) => c.kind !== "self")
              .reduce((s, c) => s + Number(c.amount || 0), 0);
          /* Platform charges = the flat platform fee (goes to admin). */
          const platform = Number(breakdown.flatAdminFee || 0);
          const payout = Number(breakdown.payoutFee || 0);

          return (
            <Stack spacing={1.1} divider={<Divider flexItem />}>
              <Stack spacing={1.1}>
                <BreakdownRow
                  label="Amount (charged to card)"
                  value={formatINR(quote.amount)}
                  strong
                />
                <BreakdownRow label="Charges" value={formatINR(charges)} />
                <BreakdownRow label="Payout" value={formatINR(payout)} />
                {/* <BreakdownRow label="Platform charges" value={formatINR(platform)} /> */}
                {self > 0 && (
                  <BreakdownRow
                    label="Your commission"
                    value={formatINR(self)}
                    strong
                  />
                )}
              </Stack>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                  border: (t) => `1px solid ${alpha(t.palette.success.main, 0.35)}`,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={700}>
                    Amount to be paid
                  </Typography>
                  <Typography fontWeight={800} fontSize={19} color="success.main">
                    {formatINR(breakdown.netToBank)}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          );
        })()}
      </Paper>
    );
  };

  /* ---------------------------------- render ---------------------------------- */

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Card to Bank Payment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pay to any bank account using a card
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
          <Button
            component={RouterLink}
            to="/finkeda/transactions"
            variant="outlined"
            size="small"
            startIcon={<ReceiptLongRoundedIcon />}
            sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 600 }}
          >
            My Transactions
          </Button>
          <Chip
            icon={<SendToMobileRoundedIcon sx={{ fontSize: 16 }} />}
            label="Card → Bank payout"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </Stack>

      {gatewaysError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setGatewaysError(null)}
        >
          {gatewaysError}
        </Alert>
      )}

      {/* Step 1 — gateway */}
      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          1. Choose a payment purpose
        </Typography>
        {renderGateways()}

        {selectedGateway && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              2. Card network &amp; type
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                  Network
                </Typography>
                <ToggleButtonGroup
                  value={network}
                  exclusive
                  size="small"
                  onChange={(_, v) => {
                    if (v) {
                      setNetwork(v);
                      setCardType("");
                    }
                  }}
                  sx={{
                    flexWrap: "wrap",
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 2,
                    },
                  }}
                >
                  {availableNetworks.map((n) => (
                    <ToggleButton key={n} value={n}>
                      {NETWORK_LABELS[n] || n}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {network && networkLimit != null && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={0.75}
                  >
                    Up to <b>{formatINR(networkLimit)}</b> per transaction
                  </Typography>
                )}
              </Box>

              {network && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                    Card type
                  </Typography>
                  <ToggleButtonGroup
                    value={cardType}
                    exclusive
                    size="small"
                    onChange={(_, v) => v && setCardType(v)}
                    sx={{
                      flexWrap: "wrap",
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 2,
                      },
                    }}
                  >
                    {availableCardTypes.map((t) => (
                      <ToggleButton key={t} value={t}>
                        {CARD_TYPE_LABELS[t] || t}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}
            </Stack>
          </>
        )}
      </Card>

      {/* Step 3 — amount + beneficiary + breakdown */}
      {selectedGateway && network && cardType && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                3. Payment details
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                The customer enters their card details on the secure payment page.
                Here you only capture the beneficiary and the amount to charge.
              </Typography>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Amount to charge"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  helperText={
                    selectedCharge?.txnLimit != null
                      ? `Limit: ${formatINR(selectedCharge.txnLimit)} per transaction`
                      : "The amount charged to the customer's card"
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    sx: { fontWeight: 700, fontSize: 17, borderRadius: 2 },
                  }}
                  inputProps={{ inputMode: "decimal" }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Self share % (optional)"
                      value={selfPct}
                      onChange={(e) =>
                        setSelfPct(e.target.value.replace(/[^0-9.]/g, ""))
                      }
                      helperText="Your commission on this txn"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        sx: { borderRadius: 2 },
                      }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Card BIN (optional)"
                      value={binValue}
                      onChange={(e) =>
                        setBinValue(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      error={!binOk}
                      helperText={
                        !binOk
                          ? "BIN must be the first 6 digits"
                          : "First 6 digits of the card"
                      }
                      inputProps={{ inputMode: "numeric", maxLength: 6 }}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Beneficiary (bank account paid out)
                  </Typography>
                </Divider>

                <TextField
                  fullWidth
                  label="Beneficiary name"
                  value={sender.senderName}
                  onChange={updateSender("senderName")}
                  error={sender.senderName !== "" && sender.senderName.trim().length < 3}
                  helperText={
                    sender.senderName !== "" && sender.senderName.trim().length < 3
                      ? "Enter the full name on the bank account"
                      : ""
                  }
                  InputProps={{ sx: { borderRadius: 2 } }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account number"
                      value={sender.senderAccount}
                      onChange={(e) =>
                        setSender((s) => ({
                          ...s,
                          senderAccount: e.target.value.replace(/[^0-9]/g, "").slice(0, 20),
                        }))
                      }
                      error={
                        sender.senderAccount !== "" &&
                        sender.senderAccount.trim().length < 6
                      }
                      helperText={
                        sender.senderAccount !== "" &&
                        sender.senderAccount.trim().length < 6
                          ? "Enter a valid account number"
                          : ""
                      }
                      inputProps={{ inputMode: "numeric" }}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="IFSC"
                      value={sender.senderIfsc}
                      onChange={(e) =>
                        setSender((s) => ({
                          ...s,
                          senderIfsc: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, "")
                            .slice(0, 11),
                        }))
                      }
                      error={sender.senderIfsc !== "" && !senderIfscOk}
                      helperText={
                        sender.senderIfsc !== "" && !senderIfscOk
                          ? "e.g. HDFC0001234"
                          : ""
                      }
                      inputProps={{ maxLength: 11, style: { textTransform: "uppercase" } }}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bank name"
                      value={sender.senderBank}
                      onChange={updateSender("senderBank")}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mobile number"
                      value={sender.senderMobile}
                      onChange={(e) =>
                        setSender((s) => ({
                          ...s,
                          senderMobile: e.target.value.replace(/\D/g, "").slice(0, 10),
                        }))
                      }
                      error={sender.senderMobile !== "" && !senderMobileOk}
                      helperText={
                        sender.senderMobile !== "" && !senderMobileOk
                          ? "Enter a valid 10-digit mobile"
                          : ""
                      }
                      inputProps={{ inputMode: "numeric" }}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PAN"
                      value={sender.senderPanNumber}
                      onChange={(e) =>
                        setSender((s) => ({
                          ...s,
                          senderPanNumber: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, "")
                            .slice(0, 10),
                        }))
                      }
                      error={sender.senderPanNumber !== "" && !senderPanOk}
                      helperText={
                        sender.senderPanNumber !== "" && !senderPanOk
                          ? "e.g. ABCDE1234F"
                          : ""
                      }
                      inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Account type</InputLabel>
                      <Select
                        value={sender.senderAccType}
                        label="Account type"
                        onChange={(e) =>
                          setSender((s) => ({ ...s, senderAccType: e.target.value }))
                        }
                        sx={{ borderRadius: 2 }}
                      >
                        {ACCT_TYPES.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t.charAt(0) + t.slice(1).toLowerCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  size="large"
                  disabled={!canProceed || submitting}
                  onClick={handleProceed}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SendToMobileRoundedIcon />
                    )
                  }
                  sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: "none", mt: 1 }}
                >
                  {submitting
                    ? "Initiating…"
                    : breakdown?.netToBank != null && amountValid
                      ? `Proceed to Pay — ${formatINR(breakdown.netToBank)} to bank`
                      : "Proceed to Pay"}
                </Button>

                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ color: "text.secondary" }}
                >
                  <LockRoundedIcon sx={{ fontSize: 15 }} />
                  <Typography variant="caption">
                    You'll be redirected to a secure payment page where the
                    customer enters their card details.
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            {renderBreakdown()}
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            sx={{ borderRadius: 2 }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
